import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  Grid,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { ImageUploadService, ImgBBService } from '../services/imageUploadService';

const ImageUploadTest: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult(null);
      setError(null);
    }
  };

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.value;
    setApiKey(key);
    // حفظ المفتاح فوراً
    ImgBBService.setApiKey(key);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('يرجى اختيار صورة أولاً');
      return;
    }

    if (!apiKey.trim()) {
      setError('يرجى إدخال مفتاح ImgBB API');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      console.log('🧪 بدء اختبار رفع الصورة...');
      
      const result = await ImageUploadService.uploadImage(selectedFile, 'imgbb');
      
      console.log('✅ نجح الرفع:', result);
      setUploadResult(result);
      
    } catch (error: any) {
      console.error('❌ فشل الرفع:', error);
      setError(error.message || 'فشل في رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          🧪 اختبار رفع الصور إلى ImgBB
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          استخدم هذه الأداة لاختبار رفع الصور إلى ImgBB والتأكد من صحة مفتاح API
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* إدخال مفتاح API */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="ImgBB API Key"
            value={apiKey}
            onChange={handleApiKeyChange}
            type="password"
            helperText="احصل على مفتاح API من https://api.imgbb.com/"
            placeholder="أدخل مفتاح ImgBB API هنا"
          />
        </Box>

        {/* اختيار الملف */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<ImageIcon />}
            fullWidth
            sx={{ py: 2 }}
          >
            اختيار صورة للرفع
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileSelect}
            />
          </Button>
          
          {selectedFile && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2">الملف المختار:</Typography>
              <Typography variant="body2">📁 {selectedFile.name}</Typography>
              <Typography variant="body2">📏 {formatFileSize(selectedFile.size)}</Typography>
              <Typography variant="body2">🎨 {selectedFile.type}</Typography>
            </Box>
          )}
        </Box>

        {/* زر الرفع */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || !apiKey.trim() || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            fullWidth
            size="large"
          >
            {uploading ? 'جاري الرفع...' : 'رفع الصورة'}
          </Button>
        </Box>

        {/* رسائل الخطأ */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
            <Typography variant="subtitle2">خطأ في الرفع:</Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {/* نتيجة الرفع */}
        {uploadResult && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<SuccessIcon />}>
            <Typography variant="subtitle2" gutterBottom>
              ✅ تم رفع الصورة بنجاح!
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={uploadResult.url}
                    alt="الصورة المرفوعة"
                  />
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2"><strong>الرابط:</strong></Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      wordBreak: 'break-all',
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    {uploadResult.url}
                  </Typography>
                  
                  <Typography variant="body2"><strong>اسم الملف:</strong> {uploadResult.fileName}</Typography>
                  <Typography variant="body2"><strong>الحجم:</strong> {formatFileSize(uploadResult.size)}</Typography>
                  <Typography variant="body2"><strong>المزود:</strong> {uploadResult.provider}</Typography>
                  
                  {uploadResult.thumbnailUrl && (
                    <>
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>الصورة المصغرة:</strong></Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          wordBreak: 'break-all',
                          bgcolor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          display: 'block'
                        }}
                      >
                        {uploadResult.thumbnailUrl}
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(uploadResult.url);
                  alert('تم نسخ الرابط!');
                }}
              >
                نسخ الرابط
              </Button>
            </Box>
          </Alert>
        )}

        {/* معلومات إضافية */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            💡 نصائح لاستخدام ImgBB:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            <li>احصل على مفتاح API مجاني من https://api.imgbb.com/</li>
            <li>الحد الأقصى لحجم الصورة: 32 ميجابايت</li>
            <li>الأنواع المدعومة: JPEG, PNG, GIF, BMP, WebP</li>
            <li>الصور تبقى متاحة بشكل دائم</li>
            <li>لا توجد حدود على عدد الصور المرفوعة</li>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ImageUploadTest;
